import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Activity, ShoppingCart, Gavel, AlertTriangle, TrendingUp, 
  Clock, Package, DollarSign, Zap, RefreshCw, Radio, Users, User, Eye, Shield,
  Home, LogOut, ChevronLeft, LayoutGrid, FileText, Tag
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useActivityTracker } from '../hooks/useActivityTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function AdminLive() {
  const { user, logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  
  // Activity tracker only runs when Live Monitor is open
  useActivityTracker();
  
  const [data, setData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [integrityChecking, setIntegrityChecking] = useState(false);
  const [lastIntegrityCheck, setLastIntegrityCheck] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [monitorRes, activityRes] = await Promise.all([
        axios.get(`${API}/admin/live-monitor`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/activity?limit=50`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      setData(monitorRes.data);
      setActivityData(activityRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch live monitor data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const runIntegrityCheck = useCallback(async () => {
    setIntegrityChecking(true);
    try {
      const response = await axios.post(`${API}/admin/data-integrity-check`, {}, { 
        headers: getAuthHeader() 
      });
      setLastIntegrityCheck(response.data);
      if (response.data.fixes_applied > 0) {
        toast.success(`Fixed ${response.data.fixes_applied} issues`);
      } else {
        toast.success('No issues found');
      }
    } catch (error) {
      console.error('Failed to run integrity check:', error);
      toast.error('Failed to run integrity check');
    } finally {
      setIntegrityChecking(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 10 seconds when enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchData]);

  // Format page name for display with product details
  const formatPage = (page, productName) => {
    if (!page) return 'Unknown';
    
    // Check for product pages first
    if (page.startsWith('/product/')) {
      const productId = page.replace('/product/', '');
      if (productName) {
        return `Viewing: ${productName}`;
      }
      return `Product: ${productId.slice(0, 8)}...`;
    }
    
    const pageMap = {
      '/': 'Landing Page',
      '/catalog': 'Browsing Catalog',
      '/cart': 'Shopping Cart',
      '/checkout': 'Checkout',
      '/login': 'Login Page',
      '/register': 'Creating Account',
      '/dashboard': 'My Profile',
      '/admin': 'Admin Dashboard',
      '/admin/users': 'Admin: Users',
      '/admin/bids': 'Admin: Auctions',
      '/admin/revenue': 'Admin: Revenue',
      '/admin/mailing-list': 'Admin: BLK LIST',
      '/admin/contact-messages': 'Admin: Support',
      '/admin/tracking': 'Admin: Tracking',
      '/admin/product-import': 'Admin: Import',
      '/admin/live': 'Admin: Live Monitor'
    };
    
    if (pageMap[page]) return pageMap[page];
    if (page.startsWith('/admin')) return 'Admin Area';
    return page;
  };

  // Time since last seen
  const timeSince = (dateStr) => {
    if (!dateStr) return '';
    const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono cursor-blink">LOADING LIVE MONITOR</div>
      </div>
    );
  }

  const activity = data?.activity || {};
  const inventory = data?.inventory || {};
  const recentOrders = data?.recent_orders || [];
  const recentBids = data?.recent_bids || [];
  const activeUsers = data?.active_users || { total: 0, authenticated: 0, anonymous: 0, users: [] };
  const trendingProducts = data?.trending_products || [];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span className="font-mono text-sm hidden sm:inline">ADMIN</span>
            </Link>
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            <Badge className="bg-green-500 text-black rounded-none border-0">LIVE</Badge>
          </div>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Admin Tab Navigation */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-none p-1.5 grid grid-cols-4 sm:grid-cols-7 w-full max-w-5xl h-auto sm:h-12 gap-1 mb-8">
          <div className="h-9 sm:h-full rounded-none bg-green-500 font-mono text-[10px] sm:text-sm flex items-center justify-center gap-1 px-1 sm:px-2 text-black">
            <Radio className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>LIVE</span>
          </div>
          <Link 
            to="/admin" 
            className="h-9 sm:h-full rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-[10px] sm:text-sm flex items-center justify-center px-1 sm:px-2 text-zinc-400 transition-colors"
          >
            OVERVIEW
          </Link>
          <Link 
            to="/admin?tab=products" 
            className="h-9 sm:h-full rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-[10px] sm:text-sm flex items-center justify-center px-1 sm:px-2 text-zinc-400 transition-colors"
          >
            PRODUCTS
          </Link>
          <Link 
            to="/admin?tab=orders" 
            className="h-9 sm:h-full rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-[10px] sm:text-sm flex items-center justify-center px-1 sm:px-2 text-zinc-400 transition-colors"
          >
            ORDERS
          </Link>
          <Link 
            to="/admin?tab=offers" 
            className="h-9 sm:h-full rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-[10px] sm:text-sm flex items-center justify-center px-1 sm:px-2 text-zinc-400 transition-colors"
          >
            OFFERS
          </Link>
          <Link 
            to="/admin?tab=plans" 
            className="h-9 sm:h-full rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-[10px] sm:text-sm flex items-center justify-center px-1 sm:px-2 text-zinc-400 transition-colors"
          >
            PLANS
          </Link>
          <Link 
            to="/admin?tab=drop" 
            className="h-9 sm:h-full rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-[10px] sm:text-sm flex items-center justify-center px-1 sm:px-2 text-zinc-400 transition-colors"
          >
            DROP
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-zinc-600'}`} />
            <h1 className="font-sans text-2xl md:text-4xl text-white tracking-tighter" data-testid="live-monitor-title">
              LIVE MONITOR
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs text-zinc-500">
              {lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()}` : ''}
            </span>
            <Button
              onClick={runIntegrityCheck}
              disabled={integrityChecking}
              variant="outline"
              size="sm"
              className="rounded-none border-zinc-700 text-yellow-500 border-yellow-500/30 hover:border-yellow-500/50"
              data-testid="data-check-btn"
            >
              <Shield className={`w-3 h-3 mr-2 ${integrityChecking ? 'animate-spin' : ''}`} />
              {integrityChecking ? 'CHECKING...' : 'DATA CHECK'}
            </Button>
            <Button
              onClick={() => setAutoRefresh(!autoRefresh)}
              variant="outline"
              size="sm"
              className={`rounded-none border-zinc-700 ${autoRefresh ? 'text-green-500 border-green-500/50' : 'text-zinc-500'}`}
              data-testid="auto-refresh-btn"
            >
              <Radio className="w-3 h-3 mr-2" />
              {autoRefresh ? 'LIVE' : 'PAUSED'}
            </Button>
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="rounded-none border-zinc-700 text-zinc-400"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Active Users Section - Full Page Version with ~20 entries */}
          <div className="border border-green-500/30 bg-zinc-900/50">
            <div className="p-4 border-b border-green-500/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-green-500/5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <Users className="w-5 h-5 text-green-500" />
                <span className="font-mono text-base text-green-400">ACTIVE ON SITE RIGHT NOW</span>
                <Badge className="bg-green-500/20 text-green-400 border-0 rounded-none text-sm ml-2">
                  {activeUsers.total || 0} TOTAL
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Badge className="bg-green-500/20 text-green-400 border-0 rounded-none text-xs">
                  {activeUsers.authenticated} LOGGED IN
                </Badge>
                {activeUsers.ip_identified > 0 && (
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-0 rounded-none text-xs">
                    {activeUsers.ip_identified} RECOGNIZED
                  </Badge>
                )}
                <Badge className="bg-zinc-700/50 text-zinc-400 border-0 rounded-none text-xs">
                  {activeUsers.anonymous} UNKNOWN
                </Badge>
              </div>
            </div>
            
            {activeUsers.users?.length > 0 ? (
              <div className="max-h-[600px] overflow-y-auto" data-testid="active-users-list">
                {activeUsers.users.map((session, idx) => (
                  <div key={session.user_id || session.session_id || idx} className="p-4 border-b border-zinc-800/50 last:border-0 hover:bg-zinc-800/30">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                          session.user_id ? 'bg-green-500' : 
                          session.is_identified_by_ip ? 'bg-yellow-500' : 'bg-zinc-600'
                        }`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {session.is_admin && (
                              <Badge className="bg-red-500/20 text-red-500 border-0 rounded-none text-xs">ADMIN</Badge>
                            )}
                            {session.is_identified_by_ip && !session.user_id && (
                              <Badge className="bg-yellow-500/20 text-yellow-500 border-0 rounded-none text-xs">IP MATCH</Badge>
                            )}
                            <span className={`font-mono text-sm ${
                              session.user_id ? 'text-white' : 
                              session.is_identified_by_ip ? 'text-yellow-400' : 'text-zinc-500'
                            }`}>
                              {session.user_id 
                                ? (session.full_name || 'Guest')
                                : session.is_identified_by_ip 
                                  ? (session.full_name || 'Unknown User')
                                  : 'Anonymous'
                              }
                            </span>
                          </div>
                          {session.user_id ? (
                            session.email && (
                              <span className="font-mono text-xs text-zinc-600 block truncate">{session.email}</span>
                            )
                          ) : session.is_identified_by_ip ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              {session.email && (
                                <span className="font-mono text-xs text-yellow-600 truncate">{session.email}</span>
                              )}
                              <span className="font-mono text-xs text-zinc-600">(not logged in)</span>
                            </div>
                          ) : (
                            session.ip_address && (
                              <span className="font-mono text-xs text-zinc-500 block">IP: {session.ip_address}</span>
                            )
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-4 pl-6 sm:pl-0">
                        <div className="flex items-center gap-2 bg-zinc-800/50 px-3 py-1.5">
                          <Eye className="w-4 h-4 text-tiffany-500" />
                          <span className="font-mono text-sm text-tiffany-400 truncate max-w-[200px]">
                            {formatPage(session.current_page, session.viewing_product)}
                          </span>
                        </div>
                        <span className="font-mono text-xs text-zinc-500 flex-shrink-0">
                          {timeSince(session.last_seen)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="font-mono text-sm text-zinc-600">No active users detected</p>
                <p className="font-mono text-xs text-zinc-700 mt-1">Users will appear here as they browse the site</p>
              </div>
            )}
          </div>

          {/* Activity Stats - Same as LiveMonitor but slightly larger */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingCart className="w-5 h-5 text-tiffany-500" />
                <span className="font-mono text-xs text-zinc-500">ORDERS (5M)</span>
              </div>
              <p className={`font-mono text-4xl ${activity.orders_5min > 0 ? 'text-tiffany-500' : 'text-zinc-600'}`}>
                {activity.orders_5min || 0}
              </p>
            </div>

            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <span className="font-mono text-xs text-zinc-500">ORDERS (15M)</span>
              </div>
              <p className={`font-mono text-4xl ${activity.orders_15min > 0 ? 'text-blue-500' : 'text-zinc-600'}`}>
                {activity.orders_15min || 0}
              </p>
            </div>

            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <Gavel className="w-5 h-5 text-purple-500" />
                <span className="font-mono text-xs text-zinc-500">BIDS (5M)</span>
              </div>
              <p className={`font-mono text-4xl ${activity.bids_5min > 0 ? 'text-purple-500' : 'text-zinc-600'}`}>
                {activity.bids_5min || 0}
              </p>
            </div>

            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                <span className="font-mono text-xs text-zinc-500">REVENUE (1H)</span>
              </div>
              <p className={`font-mono text-3xl ${activity.revenue_last_hour > 0 ? 'text-green-500' : 'text-zinc-600'}`}>
                ${(activity.revenue_last_hour || 0).toFixed(0)}
              </p>
            </div>

            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="font-mono text-xs text-zinc-500">PENDING</span>
              </div>
              <p className={`font-mono text-4xl ${data?.orders?.pending > 0 ? 'text-yellow-500' : 'text-zinc-600'}`}>
                {data?.orders?.pending || 0}
              </p>
            </div>
          </div>

          {/* Trending Products */}
          {trendingProducts.length > 0 && (
            <div className="border border-tiffany-500/30 bg-tiffany-500/5 p-4">
              <h3 className="font-mono text-base text-tiffany-400 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                WHAT USERS ARE VIEWING (1H)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
                {trendingProducts.map((product, idx) => (
                  <div key={idx} className="border border-zinc-800 p-3 bg-zinc-900/50 hover:border-tiffany-500/50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-zinc-500">#{idx + 1}</span>
                      <Badge className="bg-tiffany-500/20 text-tiffany-400 border-0 rounded-none text-xs">
                        {product.view_count} views
                      </Badge>
                    </div>
                    <p className="font-mono text-sm text-white truncate" title={product.product_name}>
                      {product.product_name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inventory Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-tiffany-500" />
                <span className="font-mono text-xs text-zinc-500">IN STOCK</span>
              </div>
              <p className="font-mono text-4xl text-tiffany-500">{inventory.in_stock || 0}</p>
            </div>

            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="font-mono text-xs text-zinc-500">SOLD OUT</span>
              </div>
              <p className={`font-mono text-4xl ${inventory.sold_out > 0 ? 'text-red-500' : 'text-zinc-600'}`}>
                {inventory.sold_out || 0}
              </p>
            </div>

            <div className="border border-zinc-800 p-5 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-orange-500" />
                <span className="font-mono text-xs text-zinc-500">LOW STOCK ITEMS</span>
              </div>
              {inventory.low_stock_items?.length > 0 ? (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {inventory.low_stock_items.map(item => (
                    <div key={item.id} className="flex items-center justify-between">
                      <span className="font-mono text-sm text-zinc-400 truncate max-w-[180px]">
                        {item.name}
                      </span>
                      <Badge className="bg-orange-500/20 text-orange-500 border-0 rounded-none text-xs">
                        {item.stock} LEFT
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-mono text-sm text-zinc-600">No low stock items</p>
              )}
            </div>
          </div>

          {/* Trending Products Section (from user activity tracking) */}
          {activityData?.popular_products?.length > 0 && (
            <div className="border border-tiffany-500/30 bg-zinc-900/50">
              <div className="p-4 border-b border-tiffany-500/20 flex items-center gap-2 bg-tiffany-500/5">
                <TrendingUp className="w-5 h-5 text-tiffany-500" />
                <span className="font-mono text-sm text-tiffany-400">TRENDING PRODUCTS (24H)</span>
                <Badge className="bg-tiffany-500/20 text-tiffany-400 border-0 rounded-none text-xs ml-2">
                  {activityData.total_events || 0} PAGE VIEWS
                </Badge>
              </div>
              <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                {activityData.popular_products.map((product, idx) => (
                  <div key={idx} className="border border-zinc-800 p-3 bg-zinc-900/50 hover:border-tiffany-500/50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-zinc-500">#{idx + 1}</span>
                      <Badge className="bg-tiffany-500/20 text-tiffany-400 border-0 rounded-none text-xs">
                        {product.views} views
                      </Badge>
                    </div>
                    <p className="font-mono text-sm text-white truncate" title={product.name}>
                      {product.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data Integrity Results */}
          {lastIntegrityCheck && (
            <div className={`border p-5 ${lastIntegrityCheck.fixes_applied > 0 ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${lastIntegrityCheck.fixes_applied > 0 ? 'text-yellow-500' : 'text-green-500'}`} />
                  <span className={`font-mono text-sm ${lastIntegrityCheck.fixes_applied > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                    DATA INTEGRITY CHECK
                  </span>
                </div>
                <Badge className={`${lastIntegrityCheck.fixes_applied > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'} border-0 rounded-none`}>
                  {lastIntegrityCheck.fixes_applied > 0 ? `${lastIntegrityCheck.fixes_applied} FIXES` : 'ALL CLEAR'}
                </Badge>
              </div>
              {lastIntegrityCheck.details?.length > 0 ? (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {lastIntegrityCheck.details.map((detail, idx) => (
                    <p key={idx} className="font-mono text-xs text-zinc-400">
                      • {detail}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="font-mono text-xs text-zinc-500">No issues detected. Data is consistent.</p>
              )}
            </div>
          )}

          {/* Recent Activity Feed */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recent Orders */}
            <div className="border border-zinc-800 bg-zinc-900/30">
              <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-tiffany-500" />
                <span className="font-mono text-sm text-zinc-400">RECENT ORDERS</span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {recentOrders.length > 0 ? (
                  recentOrders.map((order, idx) => (
                    <div key={order.id || idx} className="p-4 border-b border-zinc-800/50 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-tiffany-500">{order.id}</span>
                        <span className="font-mono text-sm text-green-500">${order.total?.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-mono text-xs text-zinc-500">{order.user_name || 'Customer'}</span>
                        <span className="font-mono text-xs text-zinc-600">
                          {new Date(order.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <p className="font-mono text-sm text-zinc-600">No recent orders</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Bids */}
            <div className="border border-zinc-800 bg-zinc-900/30">
              <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-purple-500" />
                <span className="font-mono text-sm text-zinc-400">RECENT BIDS</span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {recentBids.length > 0 ? (
                  recentBids.map((bid, idx) => (
                    <div key={bid.id || idx} className="p-4 border-b border-zinc-800/50 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-zinc-400 truncate max-w-[180px]">
                          {bid.product_name || 'Unknown Item'}
                        </span>
                        <span className="font-mono text-sm text-purple-500">${bid.amount?.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-mono text-xs text-zinc-500">{bid.bidder_name}</span>
                        <span className="font-mono text-xs text-zinc-600">
                          {new Date(bid.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <p className="font-mono text-sm text-zinc-600">No recent bids</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
